// Mapeia erros de chamadas IA do backend em mensagens amigáveis PT-BR + ação
// sugerida pro aluno. Usado em AiAssistant (tutor) e ProvaRevisaoView
// (Explicar com IA). [[feedback_user_friendly_errors]]
//
// Códigos backend (vide ../api/src/functions/student-ai-chat.ts):
//   safety_blocked     → Gemini/OpenAI recusou por política de segurança
//   content_filter     → content filter ou max_output_tokens
//   rate_limited       → 429/quota mesmo após fallback
//   all_providers_failed → todos os providers tentados falharam
//   byok_required      → servidor não compartilha chave; precisa BYOK
//   spend_limit_reached → aluno bateu teto mensal
//   provider_error     → genérico, fallback final

import { ApiError } from "./api";

export interface FriendlyAiError {
  /** Título curto pro topo do bloco de erro (ex: "Conteúdo bloqueado"). */
  title: string;
  /** Mensagem detalhada PT-BR explicando o que aconteceu e o que tentar. */
  message: string;
  /** Sugestão de ação clicável quando aplicável (abrir preferências, retry, etc.). */
  actionLabel?: string;
  /** Hint de qual ação tomar (cliente decide o que fazer). */
  action?:
    | "retry"
    | "open_byok_settings"
    | "open_spend_settings"
    | "reformulate"
    | null;
}

export function friendlyAiError(err: unknown): FriendlyAiError {
  // ApiError tem .code que mapeamos diretamente.
  if (err instanceof ApiError) {
    switch (err.code) {
      case "safety_blocked":
        return {
          title: "Conteúdo bloqueado pela IA",
          message:
            "A IA recusou essa solicitação pelas políticas de segurança dela. " +
            "Isso costuma acontecer em temas sensíveis (violência, conteúdo médico explícito, etc.). " +
            "Tente reformular a pergunta com termos diferentes.",
          actionLabel: "Tentar de novo",
          action: "retry"
        };
      case "content_filter":
        return {
          title: "IA não pôde responder",
          message:
            "A IA não conseguiu gerar uma resposta — pode ser filtro de conteúdo ou o limite de tokens foi atingido. " +
            "Tente reformular com uma pergunta mais direta ou em partes menores.",
          actionLabel: "Tentar de novo",
          action: "retry"
        };
      case "rate_limited":
        return {
          title: "Muitas requisições",
          message:
            "Os serviços de IA estão muito requisitados agora. Aguarde alguns segundos e tente de novo.",
          actionLabel: "Tentar de novo",
          action: "retry"
        };
      case "all_providers_failed":
        return {
          title: "IA temporariamente fora",
          message:
            "Os provedores de IA estão indisponíveis no momento. Tentamos automaticamente os de reserva e " +
            "nenhum respondeu. Tente de novo em alguns minutos.",
          actionLabel: "Tentar de novo",
          action: "retry"
        };
      case "byok_required":
        // Caso teórico (servidor configurou allowlist) — não promove o BYOK.
        // Apenas pede pra tentar de novo; admin que se vire com a config.
        // [[feedback 2026-05-25: não pedir pro aluno configurar chave]]
        return {
          title: "IA indisponível",
          message:
            "Não foi possível processar essa solicitação no momento. " +
            "Tente de novo em alguns segundos.",
          actionLabel: "Tentar de novo",
          action: "retry"
        };
      case "spend_limit_reached":
        return {
          title: "Limite mensal atingido",
          message:
            err.message ||
            "Você atingiu o limite mensal de gastos com IA definido nas suas configurações.",
          actionLabel: "Ajustar limite",
          action: "open_spend_settings"
        };
      case "provider_error":
        return {
          title: "IA indisponível agora",
          message:
            "Não foi possível gerar a resposta da IA. Pode ser instabilidade temporária do provedor. " +
            "Tente de novo em alguns segundos.",
          actionLabel: "Tentar de novo",
          action: "retry"
        };
    }
    // ApiError com código não-mapeado: usa a mensagem do backend (que ainda
    // é PT-BR vinda do errorJson) mas com um wrapper limpo.
    return {
      title: "Falha na IA",
      message: err.message || "Erro ao processar a solicitação.",
      actionLabel: "Tentar de novo",
      action: "retry"
    };
  }

  // Não-ApiError (network failure, parse error, etc.).
  const m = err instanceof Error ? err.message : String(err ?? "");
  if (/network|fetch|failed to fetch|offline/i.test(m)) {
    return {
      title: "Sem conexão",
      message:
        "Não consegui falar com o servidor. Verifique sua internet e tente de novo.",
      actionLabel: "Tentar de novo",
      action: "retry"
    };
  }
  return {
    title: "Erro inesperado",
    message: m || "Algo deu errado. Tente de novo.",
    actionLabel: "Tentar de novo",
    action: "retry"
  };
}
